import React, { useCallback, useState, useEffect } from 'react';
// MUI
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Zoom from '@mui/material/Zoom';
import Link from '@mui/material/Link';
import LoadingButton from '@mui/lab/LoadingButton';
import { TransitionProps } from '@mui/material/transitions';

// Components & Hooks
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';
import { Upload } from 'src/components/upload';
import { OptionModel } from 'src/types/api-response';
import { CaySam, CaySamNhatKy } from 'src/types/caysam';
import { authApiService } from 'src/api/auth-api';
import { getImageUrlWithCacheBuster } from 'src/utils/image-timestap';
import DarkModeButton from 'src/components/button/button-dark-model';

import useCaySamFilterOptions from '../../utils/use-caysam-filter-options';
import TimeLine from './time-line';
import useGetCaySam from './hooks/use-get-caysam';
import { useCaySam } from './hooks/use-losam';
import NhatKyFormDialog from './nhatKyPopup';

// --- PHẦN 1: Cấu hình hiệu ứng Zoom cho Popup ảnh ---
const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) => <Zoom ref={ref} {...props} />);

type Props = {  
  open: boolean;
  onClose: () => void;
  title: string;
  lotName: string;
  // eslint-disable-next-line react/no-unused-prop-types
  lotCode: string;
  gardenName: string;
  positionLabel: string;
  caySamData: CaySam | null;
  onUpdateCaySam: (caySamData: Partial<CaySam>) => void;
  loaiTuoiOptions: OptionModel[];
  diemSucKhoeOptions?: OptionModel[];
  // eslint-disable-next-line react/no-unused-prop-types
  loading: boolean;
  onRefresh?: (newData: CaySam) => void;
};

export default function CaySamEditDrawer({
  open,
  onClose,
  title,
  lotName,
  gardenName,
  positionLabel,
  caySamData,
  onUpdateCaySam,
  loaiTuoiOptions,
  diemSucKhoeOptions,
  onRefresh,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { tinhTrangOptions, diemSucKhoeOptions: hookDiemOptions } = useCaySamFilterOptions();
  
  // Hook lấy API Upload file
  const { 
    getCaySamNhatKy, 
    caySamNhatKy, 
    loadingNhatKy, 
    errorNhatKy,
    addDinhKemFile,
    loadingAddFile
  } = useGetCaySam();

  const {
    addNhatKyByCaySamId,
    editNhatKyByCaySamId,
    loading: caySamLoading,
    error: caySamError,
  } = useCaySam();

  const mergedDiemOptions = diemSucKhoeOptions && diemSucKhoeOptions.length > 0 ? diemSucKhoeOptions : hookDiemOptions;

  const [openNhatKyDialog, setOpenNhatKyDialog] = useState(false);
  const [selectedNhatKy, setSelectedNhatKy] = useState<CaySamNhatKy | null>(null);
  const [isInvestor, setIsInvestor] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [showTimeline, setShowTimeline] = useState(false);

  // --- State quản lý xem ảnh popup ---
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // --- State quản lý Upload PDF ---
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);

  // Check if user is investor
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await authApiService.getCurrentUser();
        const roles = (res.oneItem as any)?.htTaiKhoan?.htPhanQuyenTaiKhoans || [];
        const investor =
          Array.isArray(roles) && roles.some((r: any) => String(r?.maVaiTro) === 'nft_invester');
        if (mounted) setIsInvestor(investor);
      } catch (e) {
        if (mounted) setIsInvestor(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Load nhật ký khi drawer mở
  useEffect(() => {
    if (open && caySamData?.caySam_ID) {
      getCaySamNhatKy(String(caySamData.caySam_ID));
      setShowTimeline(false);
    }
  }, [open, caySamData?.caySam_ID, getCaySamNhatKy]);

  // Sync nhật ký từ hook vào caySamData
  useEffect(() => {
    if (caySamNhatKy && caySamNhatKy.length > 0) {
      onUpdateCaySam({ caySamNhatKys: caySamNhatKy });
    }
  }, [caySamNhatKy, onUpdateCaySam]);

  // Hiển thị lỗi
  useEffect(() => {
    if (errorNhatKy) {
      enqueueSnackbar(errorNhatKy, { variant: 'error' });
    }
  }, [errorNhatKy, enqueueSnackbar]);

  useEffect(() => {
    if (caySamError) {
      enqueueSnackbar(caySamError, { variant: 'error' });
    }
  }, [caySamError, enqueueSnackbar]);

  // Lấy nhật ký mới nhất
  const latestNhatKy = React.useMemo(() => {
    const list =
      caySamNhatKy?.filter((x): x is CaySamNhatKy => !!x) ||
      caySamData?.caySamNhatKys?.filter((x): x is CaySamNhatKy => !!x) ||
      [];

    if (!list.length) return null;

    return list.reduce((acc: CaySamNhatKy | null, cur: CaySamNhatKy) => {
      if (!acc) return cur;
      return new Date(cur.ngayGhi).getTime() > new Date(acc.ngayGhi).getTime() ? cur : acc;
    }, null as CaySamNhatKy | null);
  }, [caySamNhatKy, caySamData?.caySamNhatKys]);

  const imageUrlTongQuan = React.useMemo(
    () => getImageUrlWithCacheBuster(latestNhatKy?.hinhAnhTongQuan),
    [latestNhatKy?.hinhAnhTongQuan]
  );

  const imageUrlChiTiet = React.useMemo(
    () => getImageUrlWithCacheBuster(latestNhatKy?.hinhAnhChiTiet),
    [latestNhatKy?.hinhAnhChiTiet]
  );

  const isLatestNhatKyInCurrentMonth = React.useMemo(() => {
    if (!latestNhatKy?.ngayGhi) return false;
    const nhatKyDate = new Date(latestNhatKy.ngayGhi);
    const now = new Date();
    return (
      nhatKyDate.getFullYear() === now.getFullYear() && nhatKyDate.getMonth() === now.getMonth()
    );
  }, [latestNhatKy]);

  const base64ToFile = useCallback((base64: string, filename: string): File | null => {
    try {
      if (!base64.startsWith('data:')) return null;
      const arr = base64.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
      const bstr = atob(arr[1]);
      const n = bstr.length;
      const u8arr = new Uint8Array(n);
      for (let i = 0; i < n; i += 1) {
        u8arr[i] = bstr.charCodeAt(i);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (error) {
      console.error('Error converting base64 to file:', error);
      return null;
    }
  }, []);

  // --- LOGIC XỬ LÝ UPLOAD PDF ---
  
  const handleOpenPdfDialog = () => {
    setOpenPdfDialog(true);
  };

  const handleClosePdfDialog = () => {
    if (!loadingAddFile) {
        setOpenPdfDialog(false);
        handleDeletePdf(); 
    }
  };

  const handleDropPdf = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        enqueueSnackbar('Vui lòng chỉ chọn file PDF', { variant: 'warning' });
        return;
      }
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setSelectedPdfFile(newFile);
    }
  }, [enqueueSnackbar]);

  const handleDeletePdf = () => {
    if (selectedPdfFile) {
        URL.revokeObjectURL((selectedPdfFile as any).preview);
    }
    setSelectedPdfFile(null);
  };

  const handleSubmitImport = async () => {
    if (selectedPdfFile && caySamData?.caySam_ID) {
      const success = await addDinhKemFile([String(caySamData.caySam_ID)], selectedPdfFile);
      
      if (success) {
        enqueueSnackbar('Đính kèm file PDF thành công!', { variant: 'success' });
        handleDeletePdf();
        setOpenPdfDialog(false); 
        if (onRefresh) {
            onRefresh(caySamData); 
        }
      } else {
        enqueueSnackbar('Có lỗi xảy ra khi upload file', { variant: 'error' });
      }
    }
  };
  // ------------------------------

  const handleNhatKySubmit = async (data: Partial<CaySamNhatKy>) => {
    try {
      if (!caySamData?.caySam_ID) {
        enqueueSnackbar('Không tìm thấy ID cây sâm', { variant: 'error' });
        return;
      }
      const soLaNumber = Number(data.soLa) || 0;
      const trongLuongNumber = Number(data.trongLuong) || 0
      const submitData = {
        id: data.id,
        CaySamId: String(caySamData.caySam_ID),
        NgayGhi: new Date().toISOString(),
        SoLa: soLaNumber || 0,
        DiemSucKhoe: data.diemSucKhoe || 0,
        TinhTrang: data.tinhTrang || 1,
        HinhAnh: null as boolean | null,
        IsThongBao: data.isThongBao || false,
        trongLuong: trongLuongNumber || 0, 
        GhiChu: data.ghiChu || '',
      };

      const files: File[] = [];
      if (data.hinhAnhTongQuan) {
        const tongQuanFile = base64ToFile(data.hinhAnhTongQuan, 'tongquan.jpg');
        if (tongQuanFile) {
          files.push(tongQuanFile);
          submitData.HinhAnh = true;
        }
      }
      if (data.hinhAnhChiTiet) {
        const chiTietFile = base64ToFile(data.hinhAnhChiTiet, 'chitiet.jpg');
        if (chiTietFile) {
          files.push(chiTietFile);
          submitData.HinhAnh = false;
        }
      }
      if (files.length === 0) {
        submitData.HinhAnh = null;
      }

      const isNewEntry = !data.id || data.id === '';
      const result = isNewEntry
        ? await addNhatKyByCaySamId(submitData, files)
        : await editNhatKyByCaySamId(submitData, files);

      if (result.messCode === 1) {
        enqueueSnackbar(isNewEntry ? 'Thêm nhật ký thành công' : 'Cập nhật nhật ký thành công', {
          variant: 'success',
        });
        if (caySamData.caySam_ID) {
          await getCaySamNhatKy(String(caySamData.caySam_ID));
          setRefreshKey((prev) => prev + 1);
          if (onRefresh) {
            const updatedCaySam: CaySam = {
              ...caySamData,
              caySamNhatKys: caySamNhatKy,
            } as CaySam;
            onRefresh(updatedCaySam);
          }
        }
        setOpenNhatKyDialog(false);
        setSelectedNhatKy(null);
      } else {
        enqueueSnackbar(result.message || 'Lỗi khi cập nhật nhật ký', { variant: 'error' });
      }
    } catch (error: any) {
      console.error('Error submitting nhat ky:', error);
      enqueueSnackbar(error?.message || 'Lỗi khi cập nhật nhật ký', { variant: 'error' });
    }
  };

  const handleAddNhatKy = () => {
    const newData: CaySamNhatKy = {
      id: '',
      caySamId: caySamData?.caySam_ID || '',
      taiKhoanId: '',
      tinhTrang: 1,
      diemSucKhoe: 0,
      soLa: 0,
      hinhAnhTongQuan: '',
      hinhAnhChiTiet: '',
      ngayGhi: new Date().toISOString(),
      ghiChu: '',
      isThongBao: false, 
      trongLuong: 0,
    };
    setSelectedNhatKy(newData);
    setOpenNhatKyDialog(true);
  };

  const handleEditNhatKy = () => {
    if (latestNhatKy && isLatestNhatKyInCurrentMonth) {
      setSelectedNhatKy(latestNhatKy);
      setOpenNhatKyDialog(true);
    }
  };

  const handleViewHistory = () => {
    if (caySamData?.caySam_ID) {
      getCaySamNhatKy(String(caySamData.caySam_ID));
      setShowTimeline(true);
    }
  };

  const handleOpenPreview = (url: string | null | undefined) => {
    if (url) setPreviewImage(url);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 800 } } }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={onClose} sx={{ mr: 1 }}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {title}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Thông tin vị trí
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    label="Vườn trồng"
                    value={gardenName}
                    disabled
                    size="small"
                    InputProps={{
                      startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} />,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Lô sâm"
                    value={lotName}
                    disabled
                    size="small"
                    InputProps={{
                      startAdornment: <Iconify icon="solar:home-bold" sx={{ mr: 1 }} />,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Vị trí"
                    value={positionLabel}
                    disabled
                    size="small"
                    InputProps={{
                      startAdornment: <Iconify icon="solar:map-point-bold" sx={{ mr: 1 }} />,
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Thông tin cây sâm
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Mã cây sâm"
                    value={caySamData?.maCaySam || ''}
                    required
                    size="small"
                    disabled
                    helperText="Mã cây sâm hiện tại (không thể thay đổi)"
                    InputProps={{
                      startAdornment: (
                        <Iconify icon="solar:medal-ribbons-star-bold" sx={{ mr: 1 }} />
                      ),
                    }}
                  />

                  <FormControl fullWidth size="small" disabled>
                    <InputLabel>Tuổi cây</InputLabel>
                    <Select
                      value={caySamData?.tuoiCay_ID ?? ''}
                      label="Tuổi cây"
                      startAdornment={<Iconify icon="solar:calendar-bold" sx={{ mr: 1 }} />}
                    >
                      {loaiTuoiOptions.map((opt) => (
                        <MenuItem key={opt.value} value={Number(opt.value)}>
                          {opt.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small" disabled>
                        <InputLabel>Tình trạng</InputLabel>
                        <Select
                          value={latestNhatKy?.tinhTrang ?? ''}
                          label="Tình trạng"
                          startAdornment={<Iconify icon="solar:flag-2-bold" sx={{ mr: 1 }} />}
                        >
                          {tinhTrangOptions.map((opt) => (
                            <MenuItem key={opt.value} value={Number(opt.value)}>
                              {opt.text}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small" disabled>
                        <InputLabel>Điểm sức khỏe</InputLabel>
                        <Select
                          value={latestNhatKy?.diemSucKhoe ?? ''}
                          label="Điểm sức khỏe"
                          startAdornment={<Iconify icon="solar:check-circle-bold" sx={{ mr: 1 }} />}
                        >
                          {mergedDiemOptions?.map((opt) => (
                            <MenuItem key={opt.value} value={Number(opt.value)}>
                              {opt.text}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Số lá"
                        value={latestNhatKy?.soLa ?? ''}
                        disabled
                        size="small"
                        InputProps={{
                          startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} />,
                        }}
                      />
                       <TextField
                        fullWidth
                        label="Trọng lượng"
                        value={latestNhatKy?.trongLuong ?? ''}
                        disabled 
                        size="small"
                        InputProps={{
                          startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} />,
                        }}
                      />
                    </Stack>
              <Divider />

              {/* Thông tin sinh trưởng */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Thông tin sinh trưởng
                  </Typography>
                  {!isInvestor && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="solar:add-circle-bold" />}
                        onClick={handleAddNhatKy}
                      >
                        Thêm nhật ký
                      </Button>
                      {latestNhatKy && isLatestNhatKyInCurrentMonth && (
                        <DarkModeButton
                          variant="contained"
                          size="small"
                          startIcon={<Iconify icon="solar:pen-bold" />}
                          onClick={handleEditNhatKy}
                        >
                          Cập nhật nhật ký
                        </DarkModeButton>
                      )}
                    </Stack>
                  )}
                </Box>

                {/* Chỉ hiển thị thông tin sinh trưởng nếu có nhật ký trong tháng */}
                {latestNhatKy ? (
                  <>
                    <Stack direction="row" sx={{ pb: 2 }} spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Ảnh tổng quan
                        </Typography>
                        <Box
                          sx={{
                            border: '1px solid #eee',
                            borderRadius: 2,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#fafafa',
                            width: '100%',
                            height: 280,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.9 },
                          }}
                          onClick={() => handleOpenPreview(imageUrlTongQuan)}
                        >
                          <img
                            key={`tongquan-${refreshKey}-${latestNhatKy?.id || 'empty'}`}
                            src={imageUrlTongQuan || ''}
                            alt="Ảnh tổng quan"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Ảnh chi tiết
                        </Typography>
                        <Box
                          sx={{
                            border: '1px solid #eee',
                            borderRadius: 2,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#fafafa',
                            width: '100%',
                            height: 280,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.9 },
                          }}
                          onClick={() => handleOpenPreview(imageUrlChiTiet)}
                        >
                          <img
                            key={`chitiet-${refreshKey}-${latestNhatKy?.id || 'empty'}`}
                            src={imageUrlChiTiet || ''}
                            alt="Ảnh chi tiết"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      </Box>
                    </Stack>
                    
                    {/* === KHỐI TÀI LIỆU ĐÍNH KÈM === */}
                    <Box sx={{ mb: 3 }}>
                       {/* Header cho phần tài liệu đính kèm */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                            Tài liệu đính kèm
                        </Typography>
                        
                        {/* NÚT THÊM PDF (Chỉ hiện nếu không phải investor) */}
                        {!isInvestor && (
                            <Button 
                                size="small" 
                                variant="soft"
                                color="info"
                                startIcon={<Iconify icon="eva:cloud-upload-fill" />} 
                                onClick={handleOpenPdfDialog}
                            >
                                Thêm PDF
                            </Button>
                        )}
                      </Stack>

                      {caySamData?.caySam_DinhKems && caySamData.caySam_DinhKems.length > 0 ? (
                        <Stack spacing={1}>
                          {caySamData.caySam_DinhKems.map((dk) => (
                            <Stack
                              key={dk.idFile}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'background.neutral',
                                border: '1px dashed',
                                borderColor: 'divider',
                              }}
                            >
                              <Iconify
                                icon="solar:file-download-bold"
                                width={24}
                                sx={{ color: 'primary.main' }}
                              />

                              <Stack sx={{ flex: 1, minWidth: 0 }}>
                                <Link
                                  href={dk.dinhKemFile}
                                  target="_blank"
                                  rel="noopener"
                                  variant="body2"
                                  noWrap
                                  sx={{ fontWeight: 'medium' }}
                                >
                                  {dk.dinhKemFile.split('/').pop() || 'Tài liệu đính kèm'}
                                </Link>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(dk.thoiGian).toLocaleDateString('vi-VN')}
                                </Typography>
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}
                        >
                          Chưa có tài liệu đính kèm.
                        </Typography>
                      )}
                    </Box>
                    {/* === HẾT KHỐI DANH SÁCH ĐÍNH KÈM === */}

                 
                  </>
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      color: 'text.secondary',
                      bgcolor: '#f9f9f9',
                      borderRadius: 2,
                    }}
                  >
                    <Iconify
                      icon="solar:calendar-mark-bold"
                      sx={{ fontSize: 48, mb: 1, opacity: 0.5 }}
                    />
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      Chưa có nhật ký trong tháng này
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Vui lòng thêm nhật ký mới để xem thông tin sinh trưởng
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Lịch sử sinh trưởng */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Lịch sử sinh trưởng
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Iconify icon="solar:history-bold" />}
                    onClick={handleViewHistory}
                    disabled={!caySamData?.caySam_ID || loadingNhatKy}
                  >
                    {loadingNhatKy ? 'Đang tải...' : 'Xem lịch sử'}
                  </Button>
                </Box>

                {/* eslint-disable-next-line no-nested-ternary */}
                {showTimeline ? (
                  caySamNhatKy && caySamNhatKy.length > 0 ? (
                    <TimeLine
                      title=""
                      itemsNhatKy={caySamNhatKy.filter((x): x is CaySamNhatKy => !!x)}
                      // [MỚI] Truyền options và hàm click để xem preview
                      tinhTrangOptions={tinhTrangOptions} 
                      onImageClick={handleOpenPreview}
                    />
                  ) : (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">Chưa có dữ liệu sinh trưởng</Typography>
                    </Box>
                  )
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">
                      Nhấn &quot;Xem lịch sử&quot; để hiển thị dữ liệu sinh trưởng
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button color='error' variant="contained" onClick={onClose} fullWidth>
              Đóng
            </Button> 
          </Box>
        </Box>
      </Drawer>

      {/* Dialog nhật ký */}
      <NhatKyFormDialog
        open={openNhatKyDialog}
        onClose={() => {
          setOpenNhatKyDialog(false);
          setSelectedNhatKy(null);
        }}
        onSubmit={handleNhatKySubmit}
        nhatKyData={selectedNhatKy}
        tinhTrangOptions={tinhTrangOptions}
        diemSucKhoeOptions={mergedDiemOptions}
        loading={caySamLoading}
      />

      {/* Render Dialog xem ảnh */}
      <ImagePreviewDialog
        open={Boolean(previewImage)}
        imageUrl={previewImage}
        onClose={() => setPreviewImage(null)}
      />

      {/* --- DIALOG UPLOAD PDF --- */}
      <Dialog 
        open={openPdfDialog} 
        onClose={handleClosePdfDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
            Import dữ liệu từ PDF
            <IconButton onClick={handleClosePdfDialog} size="small" disabled={loadingAddFile}>
                <Iconify icon="eva:close-fill" />
            </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
             {!selectedPdfFile && (
                <Upload
                    file={selectedPdfFile}
                    onDrop={handleDropPdf}
                    onDelete={handleDeletePdf}
                    helperTitle="Kéo thả hoặc chọn file PDF"
                    thumbnail={false}
                    accept={{ 'application/pdf': [] }}
                    helperText={
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'text.disabled' }}>
                            *.pdf (Bắt buộc)
                        </Typography>
                    }
                    sx={{ '& .upload-zone': { height: 200, border: '2px dashed', borderColor: 'grey.300', borderRadius: 1 }}}
                />
            )}

            {selectedPdfFile && ( 
                <Box sx={{ position: 'relative', width: '100%', height: '60vh', bgcolor: 'background.neutral', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: 10, right: 20, zIndex: 9, bgcolor: 'rgba(255, 255, 255, 0.9)', borderRadius: '50%', boxShadow: 2 }}>
                        <IconButton color="error" onClick={handleDeletePdf} disabled={loadingAddFile}>
                            <Iconify icon="eva:trash-2-fill" />
                        </IconButton>
                    </Box>
                    <embed src={(selectedPdfFile as any).preview} type="application/pdf" width="100%" height="100%" style={{ border: 'none' }} />
                </Box>
            )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClosePdfDialog} color="inherit" disabled={loadingAddFile}>
                Hủy bỏ
            </Button>
            
            <LoadingButton 
                onClick={handleSubmitImport} 
                variant="contained" 
                loading={loadingAddFile}
                disabled={!selectedPdfFile}
                startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            >
                Xử lý File
            </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// --- Component ImagePreviewDialog ---
type ImagePreviewProps = {
  open: boolean;
  imageUrl: string | null;
  onClose: () => void;
};

function ImagePreviewDialog({ open, imageUrl, onClose }: ImagePreviewProps) {
  if (!imageUrl) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 9999,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.4)' },
          }}
        >
          <Iconify icon="eva:close-fill" width={32} />
        </IconButton>
      </Box>

      <Box
        component="img"
        src={imageUrl}
        alt="Preview"
        onClick={onClose}
        sx={{
          maxHeight: '90vh',
          maxWidth: '90vw',
          objectFit: 'contain',
          borderRadius: 2,
          cursor: 'zoom-out', 
          boxShadow: 24,
        }}
      />
    </Dialog>
  );
}